/* ============================================================
   src/shared/leadList.ts
   ----------------------------------------------------------------
   The offline lead list: this person's own Zoho appointments for
   today through today+7, downloaded while online (zoho-appointments
   `leads` action) and kept on the device so "Pick a lead" works with
   no internet.

   Stored under one du_ key through durableStore — electron-store on
   desktop (survives restarts), localStorage on web. The du_ prefix
   and the DURABLE_KEYS entry in accessWipe mean the "no company"
   lockout wipes it; clearOfflineCache (sign-out) removes it too.

   Rules:
   - The list belongs to one login. Read by anyone else → wiped.
   - Entries dated before today (New York) are dropped on every read.
   - A failed download never touches the stored list.
   ============================================================ */

import { supabase } from "../supabaseClient";
import { durableGet, durableRemove, durableSet } from "../durableStore";

export const LEAD_LIST_KEY = "du_zoho_lead_list";

/** A looked-up contact, as the `leads` action sends it (formatContact in
 *  supabase/functions/_shared/zohoLeads.ts). */
export type LeadContact = {
  firstName: string;
  lastName: string;
  fullName: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  email: string;
  phone: string;
};

export type OfflineLead = {
  eventId: string;
  title: string;
  startTime: string;
  contactId: string;
  contactName: string;
  contact: LeadContact | null;
};

export type StoredLeadList = {
  v: 1;
  userId: string;
  fetchedAt: number;
  leads: OfflineLead[];
};

/** The New York calendar day ("YYYY-MM-DD") an appointment falls on. */
export function nyDayOf(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/** "2:30 PM" in New York time — the same clock the day headings use, so a
 *  laptop set to another time zone can't show a time on the wrong day. */
export function nyTimeOf(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Keep only appointments dated today or later (New York days). Anything
 *  without a readable date is dropped too — it can't be shown on a day. */
export function dropPastLeads(leads: OfflineLead[], today: string): OfflineLead[] {
  return leads.filter((l) => {
    const day = nyDayOf(l.startTime);
    return !!day && day >= today;
  });
}

function parseStored(raw: string | null): StoredLeadList | null {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as StoredLeadList;
    if (!p || p.v !== 1 || typeof p.userId !== "string" || !Array.isArray(p.leads)) return null;
    return p;
  } catch {
    return null;
  }
}

/** Forget the list on this device. */
export function clearLeadList(): void {
  try {
    durableRemove(LEAD_LIST_KEY);
  } catch {
    /* best effort */
  }
}

/** Save a freshly downloaded list for `userId`, past entries already gone. */
export function saveLeadList(
  userId: string,
  leads: OfflineLead[],
  today: string,
  now: number = Date.now()
): StoredLeadList {
  const stored: StoredLeadList = { v: 1, userId, fetchedAt: now, leads: dropPastLeads(leads, today) };
  try {
    durableSet(LEAD_LIST_KEY, JSON.stringify(stored));
  } catch {
    /* storage full/blocked — the list still shows for this session */
  }
  return stored;
}

/**
 * The stored list for `userId`, with past entries removed (and the trimmed
 * list written back). Another login's list, or an unreadable one, is wiped
 * and null is returned.
 */
export function loadLeadList(userId: string | null, today: string): StoredLeadList | null {
  let raw: string | null = null;
  try {
    raw = durableGet(LEAD_LIST_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  const stored = parseStored(raw);
  if (!stored || !userId || stored.userId !== userId) {
    clearLeadList();
    return null;
  }
  const kept = dropPastLeads(stored.leads, today);
  if (kept.length !== stored.leads.length) {
    const trimmed = { ...stored, leads: kept };
    try {
      durableSet(LEAD_LIST_KEY, JSON.stringify(trimmed));
    } catch {
      /* best effort */
    }
    return trimmed;
  }
  return stored;
}

/** Group by New York day, earliest first; each day's rows by start time. */
export function groupLeadsByDay(leads: OfflineLead[]): { day: string; leads: OfflineLead[] }[] {
  const byDay = new Map<string, OfflineLead[]>();
  for (const l of leads) {
    const day = nyDayOf(l.startTime);
    if (!day) continue;
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day)!.push(l);
  }
  return Array.from(byDay.keys())
    .sort()
    .map((day) => ({
      day,
      leads: byDay.get(day)!.slice().sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime)),
    }));
}

export type LeadDownload =
  | { ok: true; leads: OfflineLead[]; linked: boolean }
  | { ok: false };

/** Download the list. Never throws — a failure is `{ ok: false }` and the
 *  caller keeps whatever is stored. */
export async function downloadLeadList(): Promise<LeadDownload> {
  try {
    const { data, error } = await supabase.functions.invoke("zoho-appointments", {
      body: { action: "leads" },
    });
    if (error) throw error;
    const leads = (data as any)?.leads;
    if (!Array.isArray(leads)) throw new Error("no leads array in response");
    return { ok: true, leads: leads as OfflineLead[], linked: (data as any)?.linked !== false };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Lead list download failed", e);
    return { ok: false };
  }
}
